'use client';

import React, { useEffect, useMemo, useState } from 'react';
import {
  Users,
  BookOpen,
  TrendingUp,
  Activity,
  GraduationCap,
  AlertTriangle,
  Calendar,
  ChevronsRight,
  Workflow,
  UsersRound,
  LayoutGrid,
  Loader2,
  Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS, es, de, it, pt, uk, ru } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { cn } from '@/lib/utils';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { BarChart } from '@/components/ui/Chart';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { useInstitutionInternalNav } from '@/pages/institution/institution-internal-nav-context';
import { isLyrraOne } from '@/lib/platform';
import {
  INST_SHELL_ACCENT_TEXT,
  INST_SHELL_ALERT_AMBER,
  INST_SHELL_ALERT_ROSE,
  INST_SHELL_CARD,
  INST_SHELL_CARD_SM,
  INST_SHELL_HEADING,
  INST_SHELL_ICON_TILE,
  INST_SHELL_MUTED,
  INST_SHELL_STAT_TILE,
} from '@/lib/institution-shell-tokens';

const SORA = { fontFamily: 'Sora, sans-serif' } as const;
const CARD = INST_SHELL_CARD;
const CARD_SM = INST_SHELL_CARD_SM;
const STAT_TILE = INST_SHELL_STAT_TILE;
const MUTED = INST_SHELL_MUTED;

const dateFnsLocales: Record<string, Locale> = {
  fr,
  en: enUS,
  es,
  de,
  it,
  pt,
  uk,
  ru,
  lb: fr,
};

type ActivityKind = 'flow_completed' | 'participant_joined' | 'flow_started';
type Period = 'week' | 'month' | 'quarter';

type DashboardResponse = {
  institution?: { name?: string };
  stats?: {
    totalUsers?: number;
    totalFlows?: number;
    publishedFlowsCount?: number;
    activeUsers?: number;
    participantsCount?: number;
    recentParticipants?: number;
    periodDays?: number;
    trends?: { newLearnersPercent?: number };
    training?: {
      totalEnrollments: number;
      completedEnrollments: number;
      completionRatePercent: number;
      avgTimePerEnrollmentMinutes: number;
      notStartedCount: number;
      stalledProgressCount: number;
    };
  };
  topFlows?: Array<{ name: string; completion: number; participants?: number }>;
  recentActivity?: Array<{
    id: string;
    kind: ActivityKind;
    at: string;
    flowTitle?: string;
    participantLabel?: string;
    email?: string;
  }>;
};

function unwrapInstitutionDashboardPayload(body: unknown): DashboardResponse | null {
  if (body == null || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  if ('data' in o && o.data != null && typeof o.data === 'object') {
    return o.data as DashboardResponse;
  }
  return body as DashboardResponse;
}

function buildActivityCalendar(
  activityDates: string[],
  locale: string,
): {
  cells: Array<{ day: number | null; active: boolean; today: boolean }>;
  monthLabel: string;
} {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const activeDays = new Set<number>();
  for (const iso of activityDates) {
    const d = new Date(iso);
    if (d.getFullYear() === year && d.getMonth() === month) activeDays.add(d.getDate());
  }
  const cells: Array<{ day: number | null; active: boolean; today: boolean }> = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push({ day: null, active: false, today: false });
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push({ day: d, active: activeDays.has(d), today: d === now.getDate() });
  }
  const monthLabel = now.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  return {
    cells,
    monthLabel: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1),
  };
}

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <div
        className="mt-1 h-5 w-1 shrink-0 rounded-full bg-[color:var(--inst-player-primary,#0C1C38)]"
        aria-hidden
      />
      <div>
        <h2 style={SORA} className={cn('text-xl font-bold', INST_SHELL_HEADING)}>
          {title}
        </h2>
        {subtitle ? <p className={cn('mt-0.5 text-xs', MUTED)}>{subtitle}</p> : null}
      </div>
    </div>
  );
}

export default function InstitutionDashboard() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const institutionNav = useInstitutionInternalNav();
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const language = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').split('-')[0];
  const dfLocale = dateFnsLocales[language] ?? enUS;

  const periodLabels: Record<Period, string> = {
    week: t('dashboard.period.week', 'Semaine'),
    month: t('dashboard.period.month', 'Mois'),
    quarter: t('dashboard.period.quarter', 'Trimestre'),
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const raw = await api.get<unknown>('/institution/dashboard', { period: selectedPeriod });
        if (!mounted) return;
        setDashboard(unwrapInstitutionDashboardPayload(raw));
        setError(null);
      } catch (err: unknown) {
        if (!mounted) return;
        const msg =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: string }).message)
            : t('institution.dashboard.loadError');
        setError(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [t, selectedPeriod]);

  const nf = useMemo(
    () => new Intl.NumberFormat(language),
    [language],
  );

  const stats = dashboard?.stats ?? {};
  const training = stats.training;
  const trendLearners =
    typeof stats.trends?.newLearnersPercent === 'number' ? stats.trends.newLearnersPercent : null;
  const completionRate = training?.completionRatePercent ?? 0;
  const recentItems = dashboard?.recentActivity ?? [];

  const flowChart = useMemo(() => {
    return (dashboard?.topFlows ?? []).map((r) => ({ name: r.name, completion: r.completion }));
  }, [dashboard?.topFlows]);

  const calendar = useMemo(
    () => buildActivityCalendar(recentItems.map((i) => i.at), language),
    [recentItems, language],
  );

  const progressCircumference = 2 * Math.PI * 44;
  const progressOffset = progressCircumference * (1 - Math.min(100, Math.max(0, completionRate)) / 100);

  const openTab = (tabId: string) => institutionNav?.openTab(tabId);

  const kpis = useMemo(
    () => {
      const items = [
        {
          label: t('dashboard.totalLearners', 'Total apprenants'),
          value: stats.participantsCount ?? 0,
          icon: Users,
        },
        {
          label: t('dashboard.publishedTraining', 'Formations publiées'),
          value: stats.publishedFlowsCount ?? stats.totalFlows ?? 0,
          icon: BookOpen,
        },
        {
          label: t('dashboard.activeUsersPeriod', 'Utilisateurs actifs (période)'),
          value: stats.activeUsers ?? 0,
          icon: TrendingUp,
        },
      ];
      if (!isLyrraOne()) {
        items.push({
          label: t('dashboard.collaborators', 'Collaborateurs'),
          value: stats.totalUsers ?? 0,
          icon: Activity,
        });
      }
      return items;
    },
    [t, stats],
  );

  const quickLinks = useMemo(
    () => [
      { tab: 'flows', icon: Workflow, labelKey: 'institution.tabs.flows' },
      { tab: 'learners', icon: Users, labelKey: 'institution.tabs.learners' },
      { tab: 'groups', icon: UsersRound, labelKey: 'institution.tabs.groups' },
      { tab: 'bi-studio', icon: LayoutGrid, labelKey: 'institution.tabs.biStudio' },
    ],
    [],
  );

  const activityBadgeVariant = (kind: ActivityKind) => {
    const map: Record<ActivityKind, 'success' | 'info' | 'warning' | 'primary'> = {
      flow_completed: 'success',
      participant_joined: 'info',
      flow_started: 'info',
    };
    return map[kind];
  };

  const activityBadgeLabel = (kind: ActivityKind) => {
    const key =
      kind === 'flow_completed' ? 'completion' : kind === 'participant_joined' ? 'enrollment' : 'start';
    return t(`institution.dashboard.activity.${key}`);
  };

  const formatActivityLine = (item: NonNullable<DashboardResponse['recentActivity']>[number]) => {
    switch (item.kind) {
      case 'flow_completed':
        return t('institution.dashboard.activity.flowCompleted', {
          name: item.participantLabel ?? '—',
          flow: item.flowTitle ?? '—',
        });
      case 'participant_joined':
        return t('institution.dashboard.activity.participantJoined', {
          email: item.email ?? item.participantLabel ?? '—',
        });
      case 'flow_started':
        return t('institution.dashboard.activity.flowStarted', {
          name: item.participantLabel ?? '—',
          flow: item.flowTitle ?? '—',
        });
      default:
        return '';
    }
  };

  const firstName = user?.firstName?.trim() || t('institution.dashboard.home.defaultName');
  const institutionName = dashboard?.institution?.name?.trim();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24">
        <Loader2 className="h-10 w-10 animate-spin text-[color:var(--inst-player-primary,#0C1C38)] dark:text-[color:var(--inst-player-accent,#5eead4)]" />
        <p className={cn('text-sm', MUTED)}>{t('institution.dashboard.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      <h1 className="sr-only">{t('institution.tabs.home', 'Accueil')}</h1>

      {/* Hero */}
      <section className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <div className={CARD}>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <p className={cn('text-[10px] font-semibold uppercase tracking-wider', MUTED)}>
                {institutionName ?? t('institution.header.workspace')}
              </p>
              <h2 style={SORA} className={cn('mt-1 text-2xl font-bold lg:text-3xl', INST_SHELL_HEADING)}>
                {t('institution.dashboard.home.greeting', { name: firstName })}
              </h2>
              <p className={cn('mt-2 max-w-lg text-sm leading-relaxed', MUTED)}>
                {t('institution.dashboard.home.greetingSubtitle')}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className={cn('text-xs font-medium', MUTED)}>
                  {t('institution.dashboard.home.periodLabel')}
                </span>
                <div className="inline-flex gap-1 rounded-lg bg-[color:color-mix(in_srgb,var(--inst-player-text)_5%,var(--inst-player-bg))] p-1">
                  {(['week', 'month', 'quarter'] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setSelectedPeriod(p)}
                      className={cn(
                        'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                        selectedPeriod === p
                          ? 'bg-[color:var(--inst-player-surface)] text-[color:var(--inst-player-text)] shadow'
                          : cn(MUTED, 'hover:text-[color:var(--inst-player-text)]'),
                      )}
                    >
                      {periodLabels[p]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => openTab('flows')}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-[14px] bg-[color:var(--inst-player-primary,#0C1C38)] px-5 text-sm font-semibold text-white transition-colors hover:bg-[color:var(--inst-player-primary-hover,#19335E)] dark:bg-[color:var(--inst-player-accent,#5eead4)] dark:text-[#0C1C38] dark:hover:opacity-90"
                >
                  <Workflow className="h-4 w-4" aria-hidden />
                  {t('institution.dashboard.home.primaryCta')}
                </button>
                <button
                  type="button"
                  onClick={() => openTab('learners')}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-[14px] border border-[color:var(--inst-player-border)] bg-[color:var(--inst-player-surface)] px-5 text-sm font-semibold text-[color:var(--inst-player-text)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--inst-player-text)_6%,var(--inst-player-surface))]"
                >
                  <Users className="h-4 w-4" aria-hidden />
                  {t('institution.dashboard.home.secondaryCta')}
                </button>
              </div>
            </div>
            {training ? (
              <div className="flex flex-col items-center gap-2 lg:items-end">
                <div className="relative h-28 w-28 sm:h-32 sm:w-32">
                  <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100" aria-hidden>
                    <circle
                      cx="50"
                      cy="50"
                      r="44"
                      fill="none"
                      stroke="#E8EDF5"
                      className="dark:stroke-[hsl(217,33%,28%)]"
                      strokeWidth="8"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="44"
                      fill="none"
                      stroke="currentColor"
                      className="text-[#0C1C38] dark:text-[color:color-mix(in_srgb,var(--inst-player-accent,#5eead4)_88%,white)]"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={progressCircumference}
                      strokeDashoffset={progressOffset}
                      style={{ transition: 'stroke-dashoffset 600ms ease' }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-[color:var(--inst-player-text)]" style={SORA}>
                      {completionRate}%
                    </span>
                  </div>
                </div>
                <span className={cn('text-xs font-medium', MUTED)}>
                  {t('institution.dashboard.home.completionRate')}
                </span>
              </div>
            ) : null}
          </div>

          <div className="mt-6 grid auto-rows-fr gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map((kpi) => {
              const showTrend =
                kpi.label === t('dashboard.totalLearners', 'Total apprenants') && trendLearners != null;
              return (
              <div
                key={kpi.label}
                className={cn('flex h-full min-h-[5.5rem] gap-3 px-3 py-3', STAT_TILE)}
              >
                <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', INST_SHELL_ICON_TILE)}>
                  <kpi.icon className="h-4 w-4" aria-hidden />
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-between">
                  <div>
                    <div className="text-lg font-bold tabular-nums text-[color:var(--inst-player-text)]" style={SORA}>
                      {nf.format(kpi.value)}
                    </div>
                    <div className={cn('line-clamp-2 text-[11px] font-medium leading-snug', MUTED)}>{kpi.label}</div>
                  </div>
                  <p
                    className={cn(
                      'mt-1 min-h-[2rem] text-[10px] font-medium leading-tight',
                      showTrend
                        ? trendLearners! >= 0
                          ? 'text-[#19335E] dark:text-[#92B5FF]'
                          : 'text-red-500 dark:text-red-400'
                        : 'invisible',
                    )}
                    aria-hidden={!showTrend}
                  >
                    {showTrend ? (
                      <>
                        {trendLearners! >= 0 ? '↑' : '↓'} {Math.abs(trendLearners!)}%{' '}
                        {t('dashboard.vsLastPeriod', 'vs période précédente')}
                      </>
                    ) : (
                      '\u00a0'
                    )}
                  </p>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        <div className={CARD_SM}>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className={cn('text-[10px] font-semibold uppercase tracking-wider', MUTED)}>
                {t('institution.dashboard.home.calendarKicker')}
              </div>
              <div className="text-sm font-bold text-[color:var(--inst-player-text)]" style={SORA}>
                {calendar.monthLabel}
              </div>
            </div>
            <Calendar className="h-5 w-5 text-[#0C1C38] dark:text-[hsl(215,25%,78%)]" aria-hidden />
          </div>
          <div className={cn('grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase', MUTED)}>
            {[
              t('learner.progress.days.mon'),
              t('learner.progress.days.tue'),
              t('learner.progress.days.wed'),
              t('learner.progress.days.thu'),
              t('learner.progress.days.fri'),
              t('learner.progress.days.sat'),
              t('learner.progress.days.sun'),
            ].map((d, i) => (
              <span key={`${d}-${i}`}>{d}</span>
            ))}
          </div>
          <div className="mt-1.5 grid grid-cols-7 gap-1">
            {calendar.cells.map((cell, i) => {
              if (cell.day === null) return <span key={`empty-${i}`} className="aspect-square" />;
              return (
                <span
                  key={`day-${cell.day}`}
                  className={cn(
                    'flex aspect-square items-center justify-center rounded-md text-[11px] font-semibold transition',
                    cell.today &&
                      'ring-2 ring-[#0C1C38] ring-offset-1 dark:ring-[color:var(--inst-player-accent,#5eead4)]',
                    cell.active
                      ? 'bg-[#0C1C38] text-white dark:bg-[color:color-mix(in_srgb,var(--inst-player-accent,#5eead4)_85%,#0C1C38)]'
                      : 'text-[#0C1C38] hover:bg-[#F0F4FA] dark:text-white dark:hover:bg-[hsl(217,33%,20%)]',
                  )}
                >
                  {cell.day}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* Alertes formation */}
      {training && (training.notStartedCount > 0 || training.stalledProgressCount > 0) ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {training.notStartedCount > 0 ? (
            <button
              type="button"
              onClick={() => openTab('learners')}
              className={cn(
                INST_SHELL_ALERT_AMBER,
                'border border-amber-200 bg-amber-50/80 hover:bg-amber-50',
              )}
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700" aria-hidden />
              <span className="text-sm font-medium text-amber-900">
                {t('institution.dashboard.home.alertNotStarted', { count: training.notStartedCount })}
              </span>
              <ChevronsRight className="ms-auto h-4 w-4 text-amber-700/60 dark:text-amber-300/70" aria-hidden />
            </button>
          ) : null}
          {training.stalledProgressCount > 0 ? (
            <button
              type="button"
              onClick={() => openTab('learners')}
              className={cn(
                INST_SHELL_ALERT_ROSE,
                'border border-rose-200 bg-rose-50/80 hover:bg-rose-50',
              )}
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-rose-700" aria-hidden />
              <span className="text-sm font-medium text-rose-900">
                {t('institution.dashboard.home.alertStalled', { count: training.stalledProgressCount })}
              </span>
              <ChevronsRight className="ms-auto h-4 w-4 text-rose-700/60 dark:text-rose-300/70" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Activité + accès rapides */}
      <section className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <SectionHeading
            title={t('dashboard.recentActivity', 'Activité récente')}
            subtitle={periodLabels[selectedPeriod]}
          />
          {recentItems.length === 0 ? (
            <p
              className={cn(
                'rounded-xl border border-dashed border-[#E8EDF5] px-4 py-8 text-center text-sm dark:border-[hsl(217,33%,28%)]',
                MUTED,
              )}
            >
              {t('institution.dashboard.activityEmpty')}
            </p>
          ) : (
            <ul className="space-y-2">
              {recentItems.map((item, index) => (
                <li
                  key={item.id}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition',
                    index === 0
                      ? 'border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,transparent)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_5%,transparent)] dark:border-[color:color-mix(in_srgb,var(--inst-player-accent,#5eead4)_30%,transparent)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-accent,#5eead4)_8%,hsl(217,33%,17%))]'
                      : 'border-[color:var(--inst-player-border)] bg-[color:var(--inst-player-surface)]',
                  )}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Badge variant={activityBadgeVariant(item.kind)}>{activityBadgeLabel(item.kind)}</Badge>
                    <span className="truncate text-sm text-[color:var(--inst-player-text)]">
                      {formatActivityLine(item)}
                    </span>
                  </div>
                  <span
                    className={cn('flex shrink-0 items-center gap-1 whitespace-nowrap text-xs', MUTED)}
                    title={item.at}
                  >
                    <Clock className="h-3 w-3" aria-hidden />
                    {formatDistanceToNow(new Date(item.at), { addSuffix: true, locale: dfLocale })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <aside className={CARD_SM}>
          <h3 className="mb-4 text-sm font-bold text-[color:var(--inst-player-text)]" style={SORA}>
            {t('institution.dashboard.home.quickAccess')}
          </h3>
          <ul className="space-y-1.5">
            {quickLinks.map(({ tab, icon: Icon, labelKey }) => (
              <li key={tab}>
                <button
                  type="button"
                  onClick={() => openTab(tab)}
                  className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[color:var(--inst-player-text)] transition hover:bg-[color:color-mix(in_srgb,var(--inst-player-text)_6%,var(--inst-player-bg))]"
                >
                  <span
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-lg transition group-hover:bg-[color:var(--inst-player-accent)] group-hover:text-[#0C1C38]',
                      INST_SHELL_ICON_TILE,
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="flex-1 truncate text-start">{t(labelKey)}</span>
                  <ChevronsRight
                    className="h-3.5 w-3.5 text-[#5B6472] opacity-60 transition group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-[hsl(215,25%,72%)]"
                    aria-hidden
                  />
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </section>

      {/* Suivi formations */}
      {training ? (
        <Card variant="outlined">
          <CardHeader>
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-[#19335E] dark:text-[#92B5FF]" />
              <div>
                <h2 className="text-lg font-semibold text-[color:var(--inst-player-text)]">
                  {t('institution.dashboard.trainingCardTitle')}
                </h2>
                <p className="text-xs text-[color:var(--inst-player-text-muted)]">
                  {t('institution.dashboard.trainingCardHint')}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid auto-rows-fr grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <div className={cn('flex h-full min-h-[5.25rem] flex-col justify-between p-3', STAT_TILE)}>
                <p className="text-xs uppercase tracking-wide text-[color:var(--inst-player-text-muted)]">
                  {t('rh.dashboard.enrollmentsTotal')}
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-[color:var(--inst-player-text)]">
                  {nf.format(training.totalEnrollments)}
                </p>
                <p className="min-h-[1rem] text-[11px] invisible" aria-hidden>{'\u00a0'}</p>
              </div>
              <div className={cn('flex h-full min-h-[5.25rem] flex-col justify-between p-3', STAT_TILE)}>
                <p className="text-xs uppercase tracking-wide text-[color:var(--inst-player-text-muted)]">
                  {t('rh.dashboard.completionRateLabel')}
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-[color:var(--inst-player-text)]">
                  {training.completionRatePercent}%
                </p>
                <p className="min-h-[1rem] text-[11px] leading-tight text-[color:var(--inst-player-text-muted)]">
                  {nf.format(training.completedEnrollments)} {t('institution.dashboard.trainingCompleted')}
                </p>
              </div>
              <div className={cn('flex h-full min-h-[5.25rem] flex-col justify-between p-3', STAT_TILE)}>
                <p className="text-xs uppercase tracking-wide text-[color:var(--inst-player-text-muted)]">
                  {t('institution.dashboard.trainingAvgMinutes')}
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-[color:var(--inst-player-text)]">
                  {training.avgTimePerEnrollmentMinutes}
                </p>
                <p className="min-h-[1rem] text-[11px] invisible" aria-hidden>{'\u00a0'}</p>
              </div>
              <div className="flex h-full min-h-[5.25rem] flex-col justify-between rounded-xl border border-amber-100 bg-amber-50/70 p-3 dark:border-amber-900/30 dark:bg-amber-950/20">
                <p className="text-xs uppercase tracking-wide text-amber-800 dark:text-amber-200">
                  {t('rh.dashboard.notStarted')}
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-[color:var(--inst-player-text)]">
                  {nf.format(training.notStartedCount)}
                </p>
                <p className="min-h-[1rem] text-[11px] invisible" aria-hidden>{'\u00a0'}</p>
              </div>
              <div className="flex h-full min-h-[5.25rem] flex-col justify-between rounded-xl border border-rose-100 bg-rose-50/70 p-3 dark:border-rose-900/30 dark:bg-rose-950/20">
                <p className="text-xs uppercase tracking-wide text-rose-800 dark:text-rose-200">
                  {t('rh.dashboard.stalled')}
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-[color:var(--inst-player-text)]">
                  {nf.format(training.stalledProgressCount)}
                </p>
                <p className="min-h-[1rem] text-[11px] invisible" aria-hidden>{'\u00a0'}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {/* Top formations */}
      <Card variant="outlined" className="dark:bg-[hsl(217,33%,17%)] dark:border-[hsl(217,33%,22%)]">
        <CardHeader>
          <div>
            <h2 className="text-lg font-semibold text-[color:var(--inst-player-text)]">
              {t('institution.dashboard.home.topFlows')}
            </h2>
            <p className="text-xs text-[color:var(--inst-player-text-muted)]">
              {t('institution.dashboard.home.topFlowsHint')}
            </p>
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          {flowChart.length === 0 ? (
            <p className="py-8 text-center text-sm text-[color:var(--inst-player-text-muted)]">
              {t('institution.dashboard.chartTopFlowsEmpty')}
            </p>
          ) : (
            <BarChart
              data={flowChart}
              xKey="name"
              series={[
                {
                  dataKey: 'completion',
                  name: t('dashboard.completionPercent', 'Complétion %'),
                  color: '#0C1C38',
                },
              ]}
              height={260}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
